/**
 * Hooks React Query des SKILLCARDS pédagogiques (migrations 0269-0274).
 *
 * - Copie de TRAVAIL (`cards` + `card_levels` + `card_lessons`) : lue/écrite en
 *   direct sous RLS (auteur ou admin).
 * - Génération du recto : edge fn `card-generate` (1 appel IA, écrit le recto).
 * - Créature origami : edge fn `card-creature-generate` (1 appel IA, écrit
 *   `cards.creature`) ; import / suppression en direct (spec validée Zod).
 * - Publication : RPC `publish_card` (instantané `card_versions`, exige le statut
 *   `review` = validation humaine).
 * - Page publique : RPC `get_public_card` (accessible sans compte).
 *
 * Tables postérieures à types.generated.ts (stale volontaire, cf. CLAUDE.md) →
 * client générique + `as unknown as`.
 */

import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { SCHOOL_LEVELS } from '@jeraisonne/db';
import {
  cardCreatureSchema,
  defaultCardQuestionXp,
  defaultRarityFor,
  isValidCardStars,
  parseCardContent,
  toCardRatingSummary,
  type CardRatingSummary,
  type CardColorTheme,
  type CardContent,
  type CardCreature,
  type CardDifficulty,
  type CardImportance,
  type CardRarity,
  type CardStatus,
  type CardType,
} from '@jeraisonne/db/cards';
import type { AutoSheetReport } from '@/features/scratch-editor/auto-sheet-report';

const db = supabase as unknown as SupabaseClient;

export interface CardRow {
  id: string;
  short_code: string;
  title: string;
  card_type: CardType;
  subject: string;
  topic: string;
  domain_label: string | null;
  when_to_use: string;
  rule: string;
  method: string;
  example: string;
  pitfalls: string;
  /** Texte d'origine de « Générer la carte » (migration 0273). */
  source_text: string;
  structured_content: CardContent;
  ai_generation_metadata: Record<string, unknown> | null;
  xp_max: number;
  difficulty: CardDifficulty;
  importance: CardImportance;
  /** Rareté (étoiles du verso) et couleur — migration 0274. */
  rarity: CardRarity;
  color_theme: CardColorTheme;
  /** Spec de la créature origami (validée au rendu) ; null = créature par défaut. */
  creature: unknown;
  /** Filiation (migration 0275) : carte dont celle-ci est la suite ; rareté = parente + 1 étoile. */
  parent_card_id: string | null;
  /** XP par bonne réponse (migration 0278) ; null = 1/100 de l'XP max. */
  question_xp: number | null;
  status: CardStatus;
  published_version: number | null;
  published_at: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  levels: string[];
  lessons: { id: string; title: string }[];
}

/** Champs saisis par le tuteur (formulaire de création / édition). */
export interface CardInput {
  title: string;
  card_type: CardType;
  subject: string;
  topic: string;
  domain_label: string | null;
  when_to_use: string;
  rule: string;
  method: string;
  example: string;
  pitfalls: string;
  /** Texte d'origine de « Générer la carte » — conservé dans la carte (migration 0273). */
  source_text: string;
  xp_max: number;
  difficulty: CardDifficulty;
  importance: CardImportance;
  rarity: CardRarity;
  color_theme: CardColorTheme;
  /** Carte parente (filiation) ; la rareté est alors imposée par le serveur. */
  parent_card_id: string | null;
  /** XP par bonne réponse ; null = défaut 1/100 de l'XP max (migration 0278). */
  question_xp: number | null;
  levels: string[];
  lesson_ids: string[];
}

const CARD_SELECT = '*, card_levels(level), card_lessons(lesson_id, lessons(id, title))';

interface RawCard extends Omit<CardRow, 'levels' | 'lessons' | 'structured_content'> {
  structured_content: unknown;
  card_levels: { level: string }[] | null;
  card_lessons: { lesson_id: string; lessons: { id: string; title: string } | null }[] | null;
}

export function toCardRow(raw: RawCard): CardRow {
  const { card_levels, card_lessons, structured_content, ...rest } = raw;
  return {
    ...rest,
    structured_content: parseCardContent(structured_content),
    levels: (card_levels ?? []).map((l) => l.level),
    lessons: (card_lessons ?? [])
      .map((l) => l.lessons)
      .filter((l): l is { id: string; title: string } => !!l),
  };
}

export const cardsQueryKey = ['cards'] as const;
export const cardQueryKey = (id: string) => ['card', id] as const;

export function useCards() {
  return useQuery({
    queryKey: cardsQueryKey,
    queryFn: async (): Promise<CardRow[]> => {
      const { data, error } = await db
        .from('cards')
        .select(CARD_SELECT)
        .order('updated_at', { ascending: false });
      if (error) throw new Error(error.message);
      return ((data ?? []) as unknown as RawCard[]).map(toCardRow);
    },
  });
}

export function useCard(cardId: string | null | undefined) {
  return useQuery({
    queryKey: cardQueryKey(cardId ?? ''),
    enabled: !!cardId,
    queryFn: async (): Promise<CardRow> => {
      const { data, error } = await db
        .from('cards')
        .select(CARD_SELECT)
        .eq('id', cardId as string)
        .single();
      if (error) throw new Error(error.message);
      return toCardRow(data as unknown as RawCard);
    },
  });
}

function cardColumns(input: CardInput) {
  const { levels: _levels, lesson_ids: _lessons, ...columns } = input;
  return columns;
}

const FILIATION_ERROR_MESSAGES: Record<string, string> = {
  card_parent_cycle: 'Cette carte ne peut pas être la parente : elle descend déjà de celle-ci.',
  card_parent_not_owned: 'Tu ne peux relier qu’une de tes propres cartes.',
  card_parent_not_found: 'Carte parente introuvable.',
  cards_parent_not_self_chk: 'Une carte ne peut pas être sa propre parente.',
  card_relations_not_self_chk: 'Une carte ne peut pas être sa propre mère.',
  card_relations_pkey: 'Cette carte est déjà une mère de celle-ci.',
};

/** Erreur d'écriture d'une carte → message lisible (gardes de filiation, migration 0275). */
export function translateCardWriteError(message: string): string {
  const key = Object.keys(FILIATION_ERROR_MESSAGES).find((k) => message.includes(k));
  return key ? (FILIATION_ERROR_MESSAGES[key] as string) : message;
}

/** Remplace les rattachements (niveaux, cours) par l'ensemble donné. */
async function replaceLinks(cardId: string, levels: string[], lessonIds: string[]) {
  const delLevels = await db.from('card_levels').delete().eq('card_id', cardId);
  if (delLevels.error) throw new Error(delLevels.error.message);
  if (levels.length > 0) {
    const ins = await db
      .from('card_levels')
      .insert(levels.map((level) => ({ card_id: cardId, level })));
    if (ins.error) throw new Error(ins.error.message);
  }
  const delLessons = await db.from('card_lessons').delete().eq('card_id', cardId);
  if (delLessons.error) throw new Error(delLessons.error.message);
  if (lessonIds.length > 0) {
    const ins = await db
      .from('card_lessons')
      .insert(lessonIds.map((lesson_id) => ({ card_id: cardId, lesson_id })));
    if (ins.error) throw new Error(ins.error.message);
  }
}

export function useCreateCard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ input, userId }: { input: CardInput; userId: string }) => {
      const { data, error } = await db
        .from('cards')
        .insert({ ...cardColumns(input), created_by: userId, status: 'draft' })
        .select('id')
        .single();
      if (error) throw new Error(translateCardWriteError(error.message));
      const id = (data as { id: string }).id;
      await replaceLinks(id, input.levels, input.lesson_ids);
      return id;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: cardsQueryKey }),
  });
}

export function useUpdateCard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: CardInput }) => {
      const { error } = await db.from('cards').update(cardColumns(input)).eq('id', id);
      if (error) throw new Error(translateCardWriteError(error.message));
      await replaceLinks(id, input.levels, input.lesson_ids);
    },
    onSettled: (_d, _e, vars) => {
      qc.invalidateQueries({ queryKey: cardQueryKey(vars.id) });
      qc.invalidateQueries({ queryKey: cardsQueryKey });
    },
  });
}

/**
 * Enregistre le recto édité. Une carte relue qu'on modifie repasse en
 * `generated` : sa relecture doit être refaite avant publication. Une carte
 * publiée reste `published` (la version en ligne ne change qu'à la republication).
 */
export function useSaveCardContent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      content,
      status,
    }: {
      id: string;
      content: CardContent;
      status: CardStatus;
    }) => {
      const nextStatus: CardStatus =
        status === 'review' ? 'generated' : status === 'draft' ? 'generated' : status;
      const { error } = await db
        .from('cards')
        .update({ structured_content: content, status: nextStatus })
        .eq('id', id);
      if (error) throw new Error(error.message);
    },
    onSettled: (_d, _e, vars) => {
      qc.invalidateQueries({ queryKey: cardQueryKey(vars.id) });
      qc.invalidateQueries({ queryKey: cardsQueryKey });
    },
  });
}

export function useSetCardStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: CardStatus }) => {
      const { error } = await db.from('cards').update({ status }).eq('id', id);
      if (error) throw new Error(error.message);
    },
    onSettled: (_d, _e, vars) => {
      qc.invalidateQueries({ queryKey: cardQueryKey(vars.id) });
      qc.invalidateQueries({ queryKey: cardsQueryKey });
    },
  });
}

export class CardFunctionError extends Error {
  constructor(
    message: string,
    public readonly code?: string,
  ) {
    super(message);
    this.name = 'CardFunctionError';
  }
}

const GENERATE_ERROR_MESSAGES: Record<string, string> = {
  budget_exceeded: 'Le budget IA du jour est atteint. Réessaie demain.',
  card_invalid: 'L’IA n’a pas produit une carte valide. Réessaie ou précise ta méthode.',
  card_archived: 'Cette carte est archivée.',
  not_found: 'Carte introuvable.',
};

async function invokeGenerate(body: { card_id: string; instructions?: string; model?: string }) {
  const { data, error } = await supabase.functions.invoke<{ ok: boolean; content: CardContent }>(
    'card-generate',
    { body },
  );
  if (error) {
    let code: string | undefined;
    let message = error.message;
    const ctx = (error as Error & { context?: Response }).context;
    if (ctx && typeof ctx.json === 'function') {
      try {
        const parsed = (await ctx.json()) as { error?: string; message?: string };
        code = parsed.error;
        message = parsed.message ?? parsed.error ?? message;
      } catch {
        /* message générique */
      }
    }
    throw new CardFunctionError((code && GENERATE_ERROR_MESSAGES[code]) || message, code);
  }
  if (!data) throw new CardFunctionError('Réponse vide du serveur.');
  return data;
}

/** Réponse de `card-info-generate` (miroir de `CardInfo`, _shared/prompts/card-info-generate.ts). */
export interface GeneratedCardInfo {
  title: string;
  card_type: CardType;
  subject: string;
  levels: string[];
  topic: string;
  domain_label: string | null;
  method: string;
  when_to_use: string;
  rule: string;
  example: string;
  pitfalls: string;
  xp_max: number;
  difficulty: CardDifficulty;
  importance: CardImportance;
  rarity: CardRarity;
}

const INFO_ERROR_MESSAGES: Record<string, string> = {
  budget_exceeded: 'Le budget IA du jour est atteint. Réessaie demain.',
  card_invalid: 'L’IA n’a pas produit une proposition valide. Réessaie ou précise ton texte.',
  invalid_input: 'Écris au moins une phrase (10 caractères) pour décrire la carte.',
};

/**
 * « Générer la carte » : l'IA déduit toutes les informations depuis un texte.
 * N'écrit rien en base — le formulaire est prérempli, régénérable à volonté.
 */
export function useGenerateCardInfo() {
  return useMutation({
    mutationFn: async (body: {
      source_text: string;
      instructions?: string;
    }): Promise<GeneratedCardInfo> => {
      const { data, error } = await supabase.functions.invoke<{
        ok: boolean;
        info: GeneratedCardInfo;
      }>('card-info-generate', { body });
      if (error) {
        let code: string | undefined;
        let message = error.message;
        const ctx = (error as Error & { context?: Response }).context;
        if (ctx && typeof ctx.json === 'function') {
          try {
            const parsed = (await ctx.json()) as { error?: string; message?: string };
            code = parsed.error;
            message = parsed.message ?? parsed.error ?? message;
          } catch {
            /* message générique */
          }
        }
        throw new CardFunctionError((code && INFO_ERROR_MESSAGES[code]) || message, code);
      }
      if (!data?.info) throw new CardFunctionError('Réponse vide du serveur.');
      return data.info;
    },
  });
}

const CREATURE_ERROR_MESSAGES: Record<string, string> = {
  ...GENERATE_ERROR_MESSAGES,
  card_invalid: 'L’IA n’a pas réussi à dessiner la créature. Réessaie.',
  invalid_input: 'La consigne est trop longue (300 caractères maximum).',
};

/** Dessine (ou redessine) la créature origami par l'IA ; écrit `cards.creature`. */
export function useGenerateCreature() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { card_id: string; instruction?: string; model?: string }) => {
      const { data, error } = await supabase.functions.invoke<{
        ok: boolean;
        creature: CardCreature;
      }>('card-creature-generate', { body });
      if (error) {
        let code: string | undefined;
        let message = error.message;
        const ctx = (error as Error & { context?: Response }).context;
        if (ctx && typeof ctx.json === 'function') {
          try {
            const parsed = (await ctx.json()) as { error?: string; message?: string };
            code = parsed.error;
            message = parsed.message ?? parsed.error ?? message;
          } catch {
            /* message générique */
          }
        }
        throw new CardFunctionError((code && CREATURE_ERROR_MESSAGES[code]) || message, code);
      }
      if (!data?.creature) throw new CardFunctionError('Réponse vide du serveur.');
      return data.creature;
    },
    onSettled: (_d, _e, vars) => {
      qc.invalidateQueries({ queryKey: cardQueryKey(vars.card_id) });
      qc.invalidateQueries({ queryKey: cardsQueryKey });
    },
  });
}

/**
 * Enregistre une créature importée (SVG retouché) ou la supprime (`null` →
 * créature par défaut). Spec revalidée ici : rien d'invalide n'atteint la base.
 */
export function useSaveCreature() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, creature }: { id: string; creature: CardCreature | null }) => {
      const value = creature === null ? null : cardCreatureSchema.parse(creature);
      const { error } = await db.from('cards').update({ creature: value }).eq('id', id);
      if (error) throw new Error(error.message);
    },
    onSettled: (_d, _e, vars) => {
      qc.invalidateQueries({ queryKey: cardQueryKey(vars.id) });
      qc.invalidateQueries({ queryKey: cardsQueryKey });
    },
  });
}

const CARD_EXERCISE_ERROR_MESSAGES: Record<string, string> = {
  ...GENERATE_ERROR_MESSAGES,
  forbidden: 'Réservé aux administrateurs.',
  level_required: 'Choisis le niveau de l’exercice.',
  invalid_level: 'Ce niveau ne fait pas partie de la carte.',
  card_invalid: 'L’IA n’a pas produit un exercice valide. Réessaie.',
};

/** Niveau d'un exercice de carte : 1 = standard progressif, 2 = moyen (×1,5 HORA), 3 = difficile (×2). */
export type CardExerciseTier = 'easy' | 'medium' | 'hard';
export const CARD_EXERCISE_TIERS: readonly CardExerciseTier[] = ['easy', 'medium', 'hard'];
export const CARD_EXERCISE_TIER_LABELS: Record<CardExerciseTier, string> = {
  easy: 'Niveau 1',
  medium: 'Niveau 2',
  hard: 'Niveau 3',
};

export interface CardExerciseBody {
  card_id: string;
  level?: string;
  model?: string;
  /** Questions à plusieurs bonnes réponses (défaut serveur : oui). */
  multiple_answers?: boolean;
  /** Warmup Niveau 2 (`medium`, ×1,5 HORA) / Niveau 3 (`hard`, ×2) ; absent = standard. */
  difficulty?: 'medium' | 'hard';
  /** Publier directement au lieu du brouillon à relire. */
  publish?: boolean;
  /** Ne rien créer si la carte a déjà un exercice de ce niveau (non archivé). */
  skip_if_exists?: boolean;
}

export interface CardExerciseResult {
  exercise_id: string;
  title?: string;
  level?: string;
  /** Déjà existant : rien n'a été généré. */
  skipped?: boolean;
  /**
   * Découpage (migration 0501) : essai gratuit (= `exercise_id`) + 3 parties.
   * `null` = exercice gardé entier (questions liées entre elles).
   */
  split?: { free: string; parts: string[] } | null;
  /** Feuilles de brouillon des questions, générées juste après (absent si rien n'a été créé). */
  sheets?: AutoSheetReport;
}

/** Avancement de la génération des feuilles de brouillon d'un exercice. */
export interface CardExerciseSheetProgress {
  done: number;
  total: number;
}

/**
 * Exercice créé → feuille de brouillon de chaque question (essai gratuit + 3 parties s'il a
 * été découpé). Module chargé à la demande (moteur, porte, moteur v2).
 */
async function withSheets(
  result: CardExerciseResult,
  onProgress?: (p: CardExerciseSheetProgress) => void,
): Promise<CardExerciseResult> {
  if (result.skipped) return result;
  const ids = result.split ? [result.split.free, ...result.split.parts] : [result.exercise_id];
  try {
    const { generateSheetsForExercises } = await import('@/features/scratch-editor/auto-sheets');
    const sheets = await generateSheetsForExercises(ids, result.level, { onProgress });
    return { ...result, sheets };
  } catch (e) {
    return {
      ...result,
      sheets: {
        questions: 0,
        published: 0,
        packs: 0,
        review: 0,
        ineligible: 0,
        notLinked: 0,
        failed: 0,
        stopped: e instanceof Error ? e.message : 'Erreur inattendue.',
      },
    };
  }
}

/** Ce que reçoivent les hooks de génération : le corps de la requête + le suivi des feuilles. */
export type CardExerciseVars = CardExerciseBody & {
  onSheetProgress?: (p: CardExerciseSheetProgress) => void;
};

async function generateCardExercise({
  onSheetProgress,
  ...body
}: CardExerciseVars): Promise<CardExerciseResult> {
  return withSheets(await invokeCardExercise(body), onSheetProgress);
}

async function invokeCardExercise(body: CardExerciseBody): Promise<CardExerciseResult> {
  const { data, error } = await supabase.functions.invoke<CardExerciseResult & { ok: boolean }>(
    'card-exercise-generate',
    { body },
  );
  if (error) {
    let code: string | undefined;
    let message = error.message;
    const ctx = (error as Error & { context?: Response }).context;
    if (ctx && typeof ctx.json === 'function') {
      try {
        const parsed = (await ctx.json()) as { error?: string; message?: string };
        code = parsed.error;
        message = parsed.message ?? parsed.error ?? message;
      } catch {
        /* message générique */
      }
    }
    throw new CardFunctionError((code && CARD_EXERCISE_ERROR_MESSAGES[code]) || message, code);
  }
  if (!data?.exercise_id) throw new CardFunctionError('Réponse vide du serveur.');
  return data;
}

/**
 * Admin : génère un exercice (10 QCM, fondamental donc gratuit) sur la carte,
 * en BROUILLON sauf `publish`, ses questions reliées à la carte (migration
 * 0278) ; `difficulty` = Warmup Niveau 2 / 3 (0295).
 */
export function useGenerateCardExercise() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: generateCardExercise,
    onSettled: (_d, _e, vars) => {
      qc.invalidateQueries({ queryKey: publicCardQueryKey(vars.card_id) });
      qc.invalidateQueries({ queryKey: cardAdminExercisesQueryKey(vars.card_id) });
    },
  });
}

// ── Admin : exercices reliés NON publiés (brouillons, en relecture, archivés) ──

export type CardAdminExerciseStatus = 'draft' | 'awaiting_review' | 'published' | 'archived';

export interface CardAdminExercise {
  id: string;
  title: string;
  status: CardAdminExerciseStatus;
  difficulty: string | null;
  level: string | null;
  /** Nombre de questions de l'exercice reliées à la carte. */
  linkedQuestions: number;
  /** Exercice découpé : 0 = essai gratuit, 1..3 = partie ; null = entier. */
  splitPart: number | null;
}

export const CARD_ADMIN_STATUS_LABELS: Record<CardAdminExerciseStatus, string> = {
  draft: 'Brouillon',
  awaiting_review: 'À relire',
  published: 'Publié',
  archived: 'Archivé',
};

interface CardLinkRow {
  exercise_id: string;
  exercise: {
    id: string;
    title: string | null;
    status: string;
    difficulty: string | null;
    level: string | null;
    split_part?: number | null;
  } | null;
}

/**
 * PUR — regroupe les liens question ⇄ carte par exercice et ne garde que les
 * exercices NON publiés (les publiés sont déjà listés par `get_public_card`).
 * Tri : brouillons/à relire d'abord, puis Niveau 1 → 3, puis titre.
 */
export function groupCardAdminExercises(rows: readonly CardLinkRow[]): CardAdminExercise[] {
  const byId = new Map<string, CardAdminExercise>();
  for (const r of rows) {
    const e = r.exercise;
    if (!e || e.status === 'published') continue;
    const cur = byId.get(e.id);
    if (cur) {
      cur.linkedQuestions += 1;
      continue;
    }
    byId.set(e.id, {
      id: e.id,
      title: e.title?.trim() || 'Exercice sans titre',
      status: e.status as CardAdminExerciseStatus,
      difficulty: e.difficulty,
      level: e.level,
      linkedQuestions: 1,
      splitPart: e.split_part ?? null,
    });
  }
  const statusRank: Record<string, number> = { draft: 0, awaiting_review: 0, archived: 1 };
  const diffRank: Record<string, number> = { easy: 1, medium: 2, hard: 3 };
  return [...byId.values()].sort(
    (a, b) =>
      (statusRank[a.status] ?? 2) - (statusRank[b.status] ?? 2) ||
      (diffRank[a.difficulty ?? ''] ?? 9) - (diffRank[b.difficulty ?? ''] ?? 9) ||
      a.title.localeCompare(b.title, 'fr'),
  );
}

export const cardAdminExercisesQueryKey = (cardId: string) =>
  ['card-admin-exercises', cardId] as const;

/** Admin : exercices reliés à la carte qui ne sont pas (encore) publiés. RLS admin. */
export function useCardAdminExercises(cardId: string, enabled: boolean) {
  return useQuery({
    queryKey: cardAdminExercisesQueryKey(cardId),
    enabled,
    queryFn: async (): Promise<CardAdminExercise[]> => {
      const { data, error } = await db
        .from('card_question_links')
        .select('exercise_id, exercise:exercises(id, title, status, difficulty, level, split_part)')
        .eq('card_id', cardId);
      if (error) throw new Error(error.message);
      return groupCardAdminExercises((data ?? []) as unknown as CardLinkRow[]);
    },
  });
}

/** Admin : publier / archiver un exercice de la carte depuis sa page. */
export function useSetCardExerciseStatus(cardId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      exerciseId,
      status,
    }: {
      exerciseId: string;
      status: 'published' | 'archived' | 'draft';
    }) => {
      // Un essai gratuit (`split_group_id` = lui-même) entraîne ses 3 parties ;
      // une partie seule ou un exercice entier ne change que lui-même.
      const { error } = await db
        .from('exercises')
        .update({ status })
        .or(`id.eq.${exerciseId},split_group_id.eq.${exerciseId}`);
      if (error) throw new Error(error.message);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: publicCardQueryKey(cardId) });
      qc.invalidateQueries({ queryKey: cardAdminExercisesQueryKey(cardId) });
      qc.invalidateQueries({ queryKey: ['tutor-exercises'] });
      qc.invalidateQueries({ queryKey: ['admin-exercises'] });
    },
  });
}

export type CardExerciseSetItem =
  | { tier: CardExerciseTier; ok: true; result: CardExerciseResult }
  | { tier: CardExerciseTier; ok: false; error: string };

/**
 * Admin : crée D'UN COUP les 3 exercices de la carte — Niveau 1 (standard),
 * Niveau 2 (×1,5 HORA), Niveau 3 (×2) — en parallèle. Un niveau déjà présent
 * est sauté (`skip_if_exists`) ; l'échec d'un niveau n'annule pas les autres.
 */
export function useGenerateCardExerciseSet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      onSheetProgress,
      ...body
    }: Omit<CardExerciseBody, 'difficulty' | 'skip_if_exists'> & {
      /** avancement des feuilles de brouillon, par niveau */
      onSheetProgress?: (tier: CardExerciseTier, p: CardExerciseSheetProgress) => void;
    }): Promise<CardExerciseSetItem[]> =>
      Promise.all(
        CARD_EXERCISE_TIERS.map(async (tier): Promise<CardExerciseSetItem> => {
          try {
            const result = await generateCardExercise({
              ...body,
              difficulty: tier === 'easy' ? undefined : tier,
              skip_if_exists: true,
              onSheetProgress: onSheetProgress && ((p) => onSheetProgress(tier, p)),
            });
            return { tier, ok: true, result };
          } catch (e) {
            return { tier, ok: false, error: e instanceof Error ? e.message : 'Échec.' };
          }
        }),
      ),
    onSettled: (_d, _e, vars) => {
      qc.invalidateQueries({ queryKey: publicCardQueryKey(vars.card_id) });
      qc.invalidateQueries({ queryKey: cardAdminExercisesQueryKey(vars.card_id) });
    },
  });
}

export function useGenerateCard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: invokeGenerate,
    onSettled: (_d, _e, vars) => {
      qc.invalidateQueries({ queryKey: cardQueryKey(vars.card_id) });
      qc.invalidateQueries({ queryKey: cardsQueryKey });
    },
  });
}

const PUBLISH_ERROR_MESSAGES: Record<string, string> = {
  card_not_reviewed: 'Valide d’abord le contenu (relecture) avant de publier.',
  card_empty: 'Le recto est vide.',
  card_no_level: 'Choisis au moins un niveau.',
  not_authorized: 'Tu n’es pas l’auteur de cette carte.',
};

export function translatePublishError(message: string): string {
  const key = Object.keys(PUBLISH_ERROR_MESSAGES).find((k) => message.includes(k));
  return key ? (PUBLISH_ERROR_MESSAGES[key] as string) : message;
}

export function usePublishCard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (cardId: string) => {
      const { data, error } = await db.rpc('publish_card', { p_card_id: cardId });
      if (error) throw new Error(translatePublishError(error.message));
      return data as { card_id: string; version: number };
    },
    onSettled: (_d, _e, cardId) => {
      qc.invalidateQueries({ queryKey: cardQueryKey(cardId) });
      qc.invalidateQueries({ queryKey: cardsQueryKey });
      qc.invalidateQueries({ queryKey: publicCardQueryKey(cardId) });
    },
  });
}

// ── Cours disponibles au rattachement ───────────────────────────────────────

export interface LessonOption {
  id: string;
  title: string;
  chapterTitle: string;
  level: string;
}

export function useLessonOptions(subject: string, levels: string[]) {
  return useQuery({
    queryKey: ['card-lesson-options', subject, [...levels].sort().join(',')],
    enabled: !!subject && levels.length > 0,
    queryFn: async (): Promise<LessonOption[]> => {
      const { data, error } = await db
        .from('lessons')
        .select('id, title, chapters!inner(title, subject, level, status)')
        .eq('status', 'published')
        .eq('chapters.subject', subject)
        .eq('chapters.status', 'published')
        .in('chapters.level', levels)
        .order('title');
      if (error) throw new Error(error.message);
      type Row = { id: string; title: string; chapters: { title: string; level: string } };
      return ((data ?? []) as unknown as Row[]).map((r) => ({
        id: r.id,
        title: r.title,
        chapterTitle: r.chapters.title,
        level: r.chapters.level,
      }));
    },
  });
}

// ── Page publique ───────────────────────────────────────────────────────────

export interface PublicCardSnapshot {
  title: string;
  card_type: CardType;
  subject: string;
  topic: string;
  domain_label: string | null;
  when_to_use: string;
  rule: string;
  method: string;
  example: string;
  pitfalls: string;
  structured_content: CardContent;
  xp_max: number;
  difficulty: CardDifficulty;
  importance: CardImportance;
  /** Absents d'un instantané antérieur à la migration 0274 → normalisés au chargement. */
  rarity: CardRarity;
  color_theme: CardColorTheme;
  creature: unknown;
  /** XP par bonne réponse, valeur effective (migration 0278 ; défaut si instantané ancien). */
  question_xp: number;
  levels: string[];
}

/** Exercice relié à la carte, publié (page publique, migration 0278). */
export interface PublicCardExercise {
  id: string;
  title: string;
  role: string;
  /** Questions de l'exercice reliées à la carte. */
  linked_questions: number;
  /** Exercice fondamental : toujours gratuit. */
  is_fundamental: boolean;
  /** Somme des multiplicateurs de difficulté des questions reliées (migration 0280). */
  linked_weight: number | null;
  /** Niveau de l'exercice (chapitre, sinon `exercises.level`) — migration 0281. */
  level: string | null;
  /** Difficulté de l'exercice (0295) : easy = Niveau 1, medium = 2 (×1,5), hard = 3 (×2). */
  difficulty?: 'easy' | 'medium' | 'hard' | null;
  /**
   * Exercice découpé (migration 0501) : 0 = essai gratuit (1 question), 1..3 =
   * parties payantes (3 questions, verrouillées sans accès). Absent/null = non découpé.
   */
  split_part?: number | null;
  /** Illustration de l'exercice (migration 0721) : vignette d'un problème en situation. */
  illustration_url?: string | null;
  /** Nombre total de questions de l'exercice (0721) : « 9 questions, dont 2 sur cette carte ». */
  question_count?: number | null;
  /** Titre de son premier thème (0721), ex. « Écologie ». */
  theme?: string | null;
}

/**
 * Exercices de la carte (fondamentaux : essais, parties, niveaux) d'un côté, problèmes en
 * situation de l'autre (exercices thématiques refaits, NON fondamentaux, reliés par
 * quelques questions — migration 0721). Ordre conservé. PUR, testé.
 */
export function splitCardExercises<T extends { is_fundamental: boolean }>(
  exercises: readonly T[],
): { drills: T[]; problems: T[] } {
  return {
    drills: exercises.filter((e) => e.is_fundamental),
    problems: exercises.filter((e) => !e.is_fundamental),
  };
}

/**
 * Exercices adaptés à l'élève : ceux de son niveau ou d'un niveau INFÉRIEUR
 * (et ceux sans niveau). Sans niveau d'élève (visiteur, tuteur, étudiant du
 * supérieur) : tous. PUR, testé.
 */
export function exercisesForStudentLevel<T extends { level: string | null }>(
  exercises: readonly T[],
  studentLevel: string | null | undefined,
): T[] {
  const order = SCHOOL_LEVELS.map((l) => l.value as string);
  const max = studentLevel ? order.indexOf(studentLevel) : -1;
  if (max < 0) return [...exercises];
  return exercises.filter((e) => {
    const rank = e.level ? order.indexOf(e.level) : -1;
    return rank < 0 || rank <= max;
  });
}

/** Carte de la filiation (migrations 0275-0276) : publiée, titre/rareté de son instantané. */
export interface PublicCardRelative {
  id: string;
  short_code: string;
  title: string;
  rarity: CardRarity | null;
}

export interface PublicCard {
  id: string;
  short_code: string;
  archived: boolean;
  version: number;
  published_at: string;
  card: PublicCardSnapshot;
  lessons: { id: string; title: string; content_md: string }[];
  exercises: PublicCardExercise[];
  /** 1re carte mère publiée (rétro-compat 0276) — préférer `parents`. */
  parent: PublicCardRelative | null;
  /** Cartes mères publiées (filiation N-N, migration 0283). */
  parents: PublicCardRelative[];
  /** Cartes dérivées publiées. */
  children: PublicCardRelative[];
  /** Popularité (migration 0283) : moyenne publique + ma note + droit de noter. */
  rating: PublicCardRating;
  /**
   * Outils de la Boîte à outils reliés par l'auteur (migration 0551, N-N),
   * dans son ordre. Identifiants du catalogue client, non vérifiés ici.
   */
  tools: string[];
}

export interface PublicCardRating extends CardRatingSummary {
  /** Ma note (compte connecté), sinon null. */
  mine: number | null;
  /** Compte réel ayant travaillé / reçu la carte, ou tuteur / admin. */
  can_rate: boolean;
}

/** Valeur brute du RPC → note normalisée (numeric Postgres renvoyé en texte). */
export function normalizePublicRating(raw: unknown): PublicCardRating {
  const r = (raw ?? {}) as { mine?: unknown; can_rate?: unknown };
  return {
    ...toCardRatingSummary(raw),
    mine: r.mine == null ? null : Number(r.mine),
    can_rate: r.can_rate === true,
  };
}

/** Instantané publié → champs 0274 garantis (repli pour une carte publiée avant). */
export function normalizePublicSnapshot(
  card: Omit<
    PublicCardSnapshot,
    'rarity' | 'color_theme' | 'creature' | 'question_xp' | 'structured_content'
  > &
    Partial<Pick<PublicCardSnapshot, 'rarity' | 'color_theme' | 'creature' | 'question_xp'>> & {
      structured_content: unknown;
    },
): PublicCardSnapshot {
  return {
    ...card,
    structured_content: parseCardContent(card.structured_content),
    rarity: card.rarity ?? defaultRarityFor(card.importance, card.difficulty),
    color_theme: card.color_theme ?? 'level',
    creature: card.creature ?? null,
    question_xp: card.question_xp ?? defaultCardQuestionXp(card.xp_max),
  };
}

/** Outils reliés (RPC, migration 0551) : liste de textes, vide avant la 0551. */
export function normalizePublicTools(raw: unknown): string[] {
  return Array.isArray(raw) ? raw.filter((t): t is string => typeof t === 'string') : [];
}

export const publicCardQueryKey = (id: string) => ['public-card', id] as const;

async function fetchPublicCard(cardId: string): Promise<PublicCard | null> {
  const { data, error } = await db.rpc('get_public_card', { p_card_id: cardId });
  if (error) throw new Error(error.message);
  if (!data) return null;
  const raw = data as PublicCard & {
    card: PublicCardSnapshot & { structured_content: unknown };
  };
  return {
    ...raw,
    card: normalizePublicSnapshot(raw.card),
    exercises: (raw.exercises ?? []).map((e) => ({
      ...e,
      linked_questions: e.linked_questions ?? 0,
      is_fundamental: !!e.is_fundamental,
      linked_weight: e.linked_weight == null ? null : Number(e.linked_weight),
      level: e.level ?? null,
      illustration_url: e.illustration_url ?? null,
      question_count: e.question_count == null ? null : Number(e.question_count),
      theme: e.theme ?? null,
    })),
    parent: raw.parent ?? raw.parents?.[0] ?? null,
    parents: raw.parents ?? (raw.parent ? [raw.parent] : []),
    children: raw.children ?? [],
    rating: normalizePublicRating(raw.rating),
    tools: normalizePublicTools(raw.tools),
  };
}

export function usePublicCard(cardId: string) {
  return useQuery({
    queryKey: publicCardQueryKey(cardId),
    queryFn: () => fetchPublicCard(cardId),
    enabled: !!cardId,
  });
}

/**
 * Plusieurs cartes publiques (les SKILLCARDS d'une question du player), même
 * cache que `usePublicCard`. Rend les cartes chargées, dans l'ordre des ids.
 */
export function usePublicCards(cardIds: readonly string[]): PublicCard[] {
  return useQueries({
    queries: cardIds.map((id) => ({
      queryKey: publicCardQueryKey(id),
      queryFn: () => fetchPublicCard(id),
      enabled: !!id,
    })),
    // fonction stable : résultat mémoïsé tant que les cartes ne changent pas
    combine: loadedPublicCards,
  });
}

function loadedPublicCards(results: ReadonlyArray<{ data?: PublicCard | null }>): PublicCard[] {
  return results.flatMap((r) => (r.data ? [r.data] : []));
}

const RATING_ERROR_MESSAGES: Record<string, string> = {
  card_rating_not_allowed:
    'Travaille ou reçois cette carte avant de la noter (compte élève ou professeur requis).',
  invalid_stars: 'Choisis une note entre 1 et 5 étoiles.',
  not_authenticated: 'Connecte-toi pour noter cette carte.',
};

export function translateRatingError(message: string): string {
  const key = Object.keys(RATING_ERROR_MESSAGES).find((k) => message.includes(k));
  return key ? (RATING_ERROR_MESSAGES[key] as string) : message;
}

/** Note la carte (1..5, modifiable) — RPC `rate_card` (migration 0283). */
export function useRateCard(cardId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (stars: number) => {
      if (!isValidCardStars(stars)) throw new Error('invalid_stars');
      const { data, error } = await db.rpc('rate_card', { p_card_id: cardId, p_stars: stars });
      if (error) throw new Error(translateRatingError(error.message));
      return data as { avg: number | string | null; count: number; mine: number };
    },
    onSuccess: (data) => {
      qc.setQueryData<PublicCard | null>(publicCardQueryKey(cardId), (prev) =>
        prev
          ? {
              ...prev,
              rating: { ...normalizePublicRating({ ...data, can_rate: true }) },
            }
          : prev,
      );
    },
  });
}

// ── Filiation N-N (migration 0283) — copie de travail, sous RLS auteur ──────

export interface CardRelationRow {
  parent_card_id: string;
  child_card_id: string;
}

export const cardRelationsQueryKey = ['card-relations'] as const;

/** Toutes les relations visibles (celles de MES cartes ; tout pour un admin). */
export function useCardRelations() {
  return useQuery({
    queryKey: cardRelationsQueryKey,
    queryFn: async (): Promise<CardRelationRow[]> => {
      const { data, error } = await db
        .from('card_relations')
        .select('parent_card_id, child_card_id');
      if (error) throw new Error(error.message);
      return (data ?? []) as CardRelationRow[];
    },
  });
}

function invalidateFiliation(qc: ReturnType<typeof useQueryClient>, childId: string) {
  void qc.invalidateQueries({ queryKey: cardRelationsQueryKey });
  void qc.invalidateQueries({ queryKey: cardsQueryKey });
  void qc.invalidateQueries({ queryKey: cardQueryKey(childId) });
}

/** Ajoute une carte mère (gardes serveur : propriété, cycle ; rareté recalculée). */
export function useAddCardParent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ parentId, childId }: { parentId: string; childId: string }) => {
      const { error } = await db
        .from('card_relations')
        .insert({ parent_card_id: parentId, child_card_id: childId });
      if (error) throw new Error(translateCardWriteError(error.message));
    },
    onSuccess: (_d, v) => invalidateFiliation(qc, v.childId),
  });
}

/** Retire une carte mère (la rareté reste celle des mères restantes, sinon libre). */
export function useRemoveCardParent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ parentId, childId }: { parentId: string; childId: string }) => {
      const { error } = await db
        .from('card_relations')
        .delete()
        .eq('parent_card_id', parentId)
        .eq('child_card_id', childId);
      if (error) throw new Error(translateCardWriteError(error.message));
    },
    onSuccess: (_d, v) => invalidateFiliation(qc, v.childId),
  });
}

export async function resolveCardShortCode(code: string): Promise<string | null> {
  const { data, error } = await db.rpc('resolve_card_short_code', { p_code: code });
  if (error) throw new Error(error.message);
  return (data as string | null) ?? null;
}

// ── Progression de l'élève connecté (migration 0271) ────────────────────────

export interface MyCardProgress {
  /** HORA COURANT (érodé à la lecture, migration 0315). */
  xp: number;
  /** HORA stocké au dernier crédit, et sa date (base de l'érosion). */
  xp_stored?: number;
  xp_updated_at?: string | null;
  xp_max: number;
  /** 0..100 — réussite sur les 50 dernières questions reliées à la carte. */
  mastery: number;
  usage_count: number;
}

/** La créature évolue quand l'élève a atteint l'XP max de la carte. */
export function isCardEvolved(progress: Pick<MyCardProgress, 'xp' | 'xp_max'> | null | undefined) {
  return !!progress && progress.xp_max > 0 && progress.xp >= progress.xp_max;
}

export const myCardProgressQueryKey = (id: string) => ['my-card-progress', id] as const;

export function useMyCardProgress(cardId: string, enabled: boolean) {
  return useQuery({
    queryKey: myCardProgressQueryKey(cardId),
    enabled,
    queryFn: async (): Promise<MyCardProgress> => {
      const { data, error } = await db.rpc('get_my_card_progress', { p_card_id: cardId });
      if (error) throw new Error(error.message);
      return data as MyCardProgress;
    },
  });
}

/**
 * Nombre de SKILLCARDS déjà travaillées par l'élève (une ligne
 * `student_cards` naît au 1er crédit d'XP — migration 0271). RLS own_select.
 */
export function useMyCardCount(studentId: string) {
  return useQuery({
    queryKey: ['my-card-count', studentId],
    enabled: !!studentId,
    staleTime: 60_000,
    queryFn: async (): Promise<number> => {
      const { count, error } = await db
        .from('student_cards')
        .select('card_id', { count: 'exact', head: true })
        .eq('student_id', studentId);
      if (error) throw new Error(error.message);
      return count ?? 0;
    },
  });
}

// ── Actions groupées (liste /tutor/cards) ───────────────────────────────────

export interface BulkCardResult {
  done: number;
  failed: number;
}

/**
 * Applique un plan d'actions groupées (`planBulkCardAction`, `./bulk`).
 * Changements de statut : une requête par statut cible (`in`) sous RLS ;
 * publication : une RPC `publish_card` par carte (instantané), en série, avec
 * progression. Un échec n'arrête pas les autres cartes.
 */
export function useBulkCardAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      targets,
      publish,
      onProgress,
    }: {
      targets: { id: string; nextStatus: CardStatus | null }[];
      publish: boolean;
      onProgress?: (done: number, total: number) => void;
    }): Promise<BulkCardResult> => {
      let done = 0;
      let failed = 0;
      if (publish) {
        for (const t of targets) {
          const { error } = await db.rpc('publish_card', { p_card_id: t.id });
          if (error) failed++;
          else done++;
          onProgress?.(done + failed, targets.length);
        }
        return { done, failed };
      }
      const byStatus = new Map<CardStatus, string[]>();
      for (const t of targets) {
        if (!t.nextStatus) continue;
        byStatus.set(t.nextStatus, [...(byStatus.get(t.nextStatus) ?? []), t.id]);
      }
      for (const [status, ids] of byStatus) {
        // Lots de 100 : un `in()` de centaines d'UUID dépasse la longueur d'URL.
        for (let i = 0; i < ids.length; i += 100) {
          const chunk = ids.slice(i, i + 100);
          const { error } = await db.from('cards').update({ status }).in('id', chunk);
          if (error) failed += chunk.length;
          else done += chunk.length;
          onProgress?.(done + failed, targets.length);
        }
      }
      return { done, failed };
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: cardsQueryKey });
      qc.invalidateQueries({ queryKey: ['card'] });
    },
  });
}
